import React from 'react';
import { useTranslate } from 'nucleus-text';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { useThemeValues } from '@context/ThemeProvider';
import SpotChatBubblesSVG from '@illustrations/SpotChatBubblesSVG';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@cvent/blocks/hooks/useTheme';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import { CSSObject } from '@emotion/react';
import Typography from '@cvent/blocks/components/Typography';

const useStyles = (flyoutStyle: boolean): Record<string, CSSObject> => {
  const { font } = useTheme();
  const { isMobile } = useBreakpoints();
  const emptyBodyFontSize = isMobile ? font.prose.size.s : font.prose.size.m;

  return {
    container: {
      textAlign: 'center'
    },
    emptyStateHeader: {
      marginTop: 8,
      marginBottom: flyoutStyle ? 8 : null
    },
    emptyStateBody: {
      color: flyoutStyle ? font.color.soft : font.color.base,
      fontSize: flyoutStyle ? font.base.size[3] : emptyBodyFontSize,
      fontWeight: font.mono.weight.regular
    }
  };
};

type EmptyStateProps = {
  flyoutStyle?: boolean;
};

/**
 * Component for rendering empty state if the user has no channels
 */
function EmptyState({ flyoutStyle }: EmptyStateProps): JSX.Element {
  const { translate: t } = useTranslate();
  const { hideIllustrations } = useThemeValues();
  const styles = useStyles(flyoutStyle);
  const { isFeatureSettingEnabled } = useAttendeeHubConfig();
  const isAttendeeListEnabled = isFeatureSettingEnabled('ATTENDEE_LIST');
  const bodyText = flyoutStyle ? t('AttendeeMessaging-Empty-Body-Flyout') : t('AttendeeMessaging-Empty-Body');

  return (
    <div css={styles.container}>
      {!hideIllustrations && (
        <div {...injectTestId('attendee-messaging-empty-state-image')}>
          <SpotChatBubblesSVG flyoutStyle={flyoutStyle} />
        </div>
      )}
      <Typography
        as={flyoutStyle ? 'h3' : 'h2'}
        styledAs={flyoutStyle ? 'h4' : 'h2'}
        css={styles.emptyStateHeader}
        {...injectTestId('attendee-messaging-empty-state-header')}
      >
        {isAttendeeListEnabled
          ? t('AttendeeMessaging-Empty-Title')
          : t('AttendeeMessaging-Empty-Title-RestrictedAttendeeList')}
      </Typography>
      <div css={styles.emptyStateBody} {...injectTestId('attendee-messaging-empty-state-body')}>
        {isAttendeeListEnabled ? bodyText : t('AttendeeMessaging-Empty-Title-RestrictedAttendeeList-HelperText')}
      </div>
    </div>
  );
}

export default EmptyState;
